import http from './http';

/**
 * Obtiene el conteo de clientes para reporte de inventario de mercado.
 * Requiere al menos 1 filtro.
 * @param {Object} params - zoneId, stateId, cityId, fieldTypeId, jerseySizeId, shoeSize
 */
export async function getClientsCountReport(params) {
  const res = await http.get('/reports/clients-count', { params });
  return res.data;
}

/**
 * Conteo de clientes por rol (perfil/subrol) para reporte mapa de calor.
 * Requiere al menos 1 filtro.
 * @param {Object} params - zoneId, stateId, cityId, roleId, subRoleId
 */
export async function getClientsCountByRole(params) {
  const res = await http.get('/reports/clients-count-by-role', { params });
  return res.data;
}

/**
 * Datos por estado para mapa de calor (perfiles por estado).
 * @param {Object} params - zoneId, stateId, cityId, roleId, subRoleId
 */
export async function getHeatmapByState(params) {
  const res = await http.get('/reports/heatmap-by-state', { params });
  return res.data;
}

/**
 * Conteo de clientes por demographics (segmentación por momento de consumo / intereses).
 * Requiere al menos 1 filtro.
 * @param {Object} params - zoneId, stateId, cityId, genderId, ageRange, interestId
 */
export async function getClientsCountByDemographics(params) {
  const res = await http.get('/reports/clients-count-by-demographics', {
    params,
  });
  return res.data;
}

/**
 * Reporte de kit check.
 * Requiere al menos 1 filtro.
 * @param {Object} params - zoneId, stateId, cityId, genderId, ageRange, interestId
 */
export async function getKitCheckReport(params) {
  const res = await http.get('/reports/kit-check', { params });
  return res.data;
}

/**
 * Reporte de ventas de membresías y videos extra.
 * @param {Object} params - regionId, stateId, period (month|60d|90d|custom), dateFrom, dateTo
 */
export async function getSalesReport(params) {
  const res = await http.get('/reports/sales', { params });
  return res.data;
}

/**
 * Conteo de clientes por equipo favorito (tribuna digital).
 * Requiere al menos 1 filtro.
 * @param {Object} params - zoneId, stateId, cityId, teamId
 */
export async function getClientsCountByFavoriteTeam(params) {
  const res = await http.get('/reports/clients-count-by-favorite-team', {
    params,
  });
  return res.data;
}

/**
 * Listado paginado de videos por competencia para descarga.
 * Debe enviarse al menos un filtro (p. ej. competitionId, modalityId, periodo, etc.).
 * @param {Object} params - competitionId, modalityId, periodId, isFinalist, regionId, stateId, cityId, participantSearch, startDate, endDate, page, pageSize, sortField ('position'), sortOrder ('asc'|'desc')
 */
export async function getCompetitionVideos(params) {
  const res = await http.get('/reports/competition-videos', { params });
  return res.data?.data ?? res.data;
}

export async function getDownloadableCompetitionVideos(params) {
  const res = await http.get('/reports/competition-videos/download', { params });
  return res.data?.data ?? res.data;
}

/**
 * Detalle de reportes de un video de competencia.
 * @param {number|string} videoId
 */
export async function getCompetitionVideoReports(videoId) {
  const res = await http.get(`/reports/competition-videos/${videoId}/reports`);
  return res.data?.data ?? res.data;
}

/**
 * Detalle de un reporte de video.
 * @param {number|string} reportId
 */
export async function getVideoReportDetail(reportId) {
  const res = await http.get(`/reports/video-reports/${reportId}`);
  return res.data?.data ?? res.data;
}

/**
 * Detalle de un reporte de comentario.
 * @param {number|string} reportId
 */
export async function getCommentReportDetail(reportId) {
  const res = await http.get(`/reports/comment-reports/${reportId}`);
  return res.data?.data ?? res.data;
}

/**
 * Aplica una acción de moderación sobre un reporte de video.
 * Requiere permiso moderation.act.
 * @param {number|string} reportId
 * @param {'DELETE_VIDEO'|'BLOCK_USER'|'NO_ACTION'} action
 */
export async function applyVideoModerationAction(reportId, action) {
  const res = await http.post(
    `/reports/video-reports/${reportId}/moderation-action`,
    { action },
    { skipAccessDeniedRedirect: true }
  );
  return res.data?.data ?? res.data;
}

/**
 * Aplica una acción de moderación sobre un reporte de comentario.
 * Requiere permiso moderation.act.
 * @param {number|string} reportId
 * @param {'DELETE_COMMENT'|'BLOCK_USER'|'DELETE_COMMENT_AND_BLOCK_USER'|'NO_ACTION'} action
 */
export async function applyCommentModerationAction(reportId, action) {
  const res = await http.post(
    `/reports/comment-reports/${reportId}/moderation-action`,
    { action },
    { skipAccessDeniedRedirect: true }
  );
  return res.data?.data ?? res.data;
}

/**
 * Quita el baneo administrativo de un Client (isBanned = false).
 * Requiere permiso moderation.act.
 * @param {number|string} clientId
 */
export async function unbanClient(clientId) {
  const res = await http.patch(
    `/reports/clients/${clientId}/unban`,
    {},
    { skipAccessDeniedRedirect: true }
  );
  return unwrapReportPayload(res);
}

export async function getScoresById(id) {
  const res = await http.get('/reports/scores/'+id, {  });
  return res.data?.data ?? res.data;
}

function unwrapReportPayload(res) {
  return res.data?.data ?? res.data;
}

/**
 * Conserva totalPendingSignalCount solo si el backend lo envía de forma explícita
 * (en el payload interno o en el sobre de la respuesta). No lo calcula.
 */
function preserveExplicitPendingCount(payload, res) {
  if (!payload || typeof payload !== 'object') return payload;
  if (Object.prototype.hasOwnProperty.call(payload, 'totalPendingSignalCount')) {
    return payload;
  }
  const outer = res?.data;
  if (
    outer &&
    typeof outer === 'object' &&
    Object.prototype.hasOwnProperty.call(outer, 'totalPendingSignalCount')
  ) {
    return {
      ...payload,
      totalPendingSignalCount: outer.totalPendingSignalCount,
    };
  }
  return payload;
}

/**
 * Listado paginado de usuarios bloqueados/ocultados por otros usuarios.
 * @param {Object} params - page, pageSize
 */
export async function getHiddenUsers(params) {
  const res = await http.get('/reports/hidden-users', { params });
  return preserveExplicitPendingCount(unwrapReportPayload(res), res);
}

/**
 * Detalle de un usuario bloqueado y las señales asociadas.
 * @param {number|string} clientId
 */
export async function getHiddenUserDetail(clientId) {
  const res = await http.get(`/reports/hidden-users/${clientId}`);
  return unwrapReportPayload(res);
}

/**
 * Marca una señal de bloqueo como REVIEWED. No desbloquea al usuario.
 * @param {number|string} signalId
 */
export async function reviewHiddenUserSignal(signalId) {
  const res = await http.patch(
    `/reports/hidden-user-signals/${signalId}/review`
  );
  return unwrapReportPayload(res);
}
